<template>
  <div v-if="content" :class="{ 'shadow-xs': isShowLessButtonVisible }">
    <div
      ref="textContainerRef"
      class="text-container relative rounded-t-md border border-divider p-4 whitespace-pre-wrap"
      :class="[
        isLoadMoreButtonVisible ? 'overflow-hidden pb-0' : 'pb-4',
        { 'rounded-b-md shadow-xs': !isShowLessButtonVisible },
        maxHeightClass,
      ]"
    >
      <div
        class="break-word text-headline"
        data-cy="show-content"
        @click="emit('text:click')"
        v-html="content"
      />
      <div
        v-if="isLoadMoreButtonVisible"
        class="absolute bottom-0 left-0 flex w-full cursor-pointer items-center justify-center space-x-2 bg-show-more-white-gradient pt-4 pb-2 text-primary"
        @click="handleShouldLoadMoreButtonClick"
      >
        <icon-base
          :icon="IconAdd"
          width="20"
          height="20"
          class="scale-75 transform"
        />
        <span>{{ $t("COMMON.SHOW_MORE") }}</span>
        <icon-base
          :icon="IconAdd"
          width="20"
          height="20"
          class="scale-75 transform"
        />
      </div>
    </div>
    <div
      v-if="isShowLessButtonVisible"
      class="show-button flex w-full cursor-pointer items-center justify-center space-x-2 rounded-b-md border border-t-0 p-2 text-primary"
      @click="handleShouldShowLoadLessButtonClick"
    >
      <icon-base :icon="IconMinus" class="scale-75 transform" />
      <span>{{ $t("COMMON.SHOW_LESS") }}</span>
      <icon-base :icon="IconMinus" class="scale-75 transform" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import IconMinus from "@/components/icons/IconMinus.vue";
import IconAdd from "@/components/icons/deals/IconAdd.vue";

const DEFAULT_MAX_HEIGHT = 110;

const { content, initialMaxHeight = DEFAULT_MAX_HEIGHT } = defineProps<{
  content: string;
  initialMaxHeight?: 80 | 110 | 600;
}>();

const emit = defineEmits<{ "text:click": []; "button:click": [] }>();

const textContainerRef = ref<HTMLDivElement | null>(null);
const shouldShowLoadMoreButton = ref(true);
const shouldShowLoadLessButton = ref(false);

const textContainerHeight = ref(textContainerRef.value?.scrollHeight || 0);

const isContentTooLong = computed(() => {
  return textContainerHeight.value > initialMaxHeight;
});

const maxHeightClass = computed(() => {
  // tailwind discards interpolated arbitrary values
  // need full class name in source
  const heightMap = {
    80: "max-h-20",
    110: "max-h-30",
    600: "max-h-240",
  };
  return isLoadMoreButtonVisible.value ? heightMap[initialMaxHeight] : "";
});

const isLoadMoreButtonVisible = computed(() => {
  return isContentTooLong.value && shouldShowLoadMoreButton.value;
});

const isShowLessButtonVisible = computed(() => {
  return isContentTooLong.value && shouldShowLoadLessButton.value;
});

const handleShouldShowLoadLessButtonClick = () => {
  shouldShowLoadMoreButton.value = true;
  shouldShowLoadLessButton.value = false;
  emit("button:click");
};

const handleShouldLoadMoreButtonClick = () => {
  shouldShowLoadMoreButton.value = false;
  shouldShowLoadLessButton.value = true;
  emit("button:click");
};

watch(
  () => [content, textContainerRef.value],
  () => {
    setTimeout(() => {
      textContainerHeight.value = textContainerRef.value?.scrollHeight || 0;
    }, 0);
  }
);
</script>
